import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getVoicemailDrops,
  createVoicemailDrop,
  replaceVoicemailDrop,
  renameVoicemailDrop,
  deleteVoicemailDrop
} from 'services/voicemail-drop';
import { openSnackbar } from 'store/slices/snackbar';
import { dispatch } from 'store';

const QUERY_KEY = ['voicemailDrops'];

// `userId` below is a teammate an admin is managing (see useTeammateId); omit it for your own
// library. A teammate's list sits under the same prefix, so every invalidation here covers it, and
// under 'user' so it cannot collide with the Power Dialer's ['voicemailDrops', kind].
export const useVoicemailDrops = (userId?: string) => {
  return useQuery({
    queryKey: userId ? [...QUERY_KEY, 'user', userId] : QUERY_KEY,
    queryFn: () => getVoicemailDrops(userId),
    refetchOnWindowFocus: false
  });
};

export const useCreateVoicemailDrop = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, file, duration }: { name: string; file: File; duration?: number }) =>
      createVoicemailDrop(name, file, duration, undefined, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      dispatch(
        openSnackbar({
          open: true,
          message: 'Voicemail drop created successfully',
          variant: 'alert',
          alert: { color: 'success' },
          close: false
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          open: true,
          message: 'Failed to create voicemail drop. Please try again.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
    }
  });
};

export const useReplaceVoicemailDrop = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file, name, duration }: { id: string; file: File; name?: string; duration?: number }) =>
      replaceVoicemailDrop(id, file, name, duration, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      dispatch(
        openSnackbar({
          open: true,
          message: 'Voicemail drop replaced successfully',
          variant: 'alert',
          alert: { color: 'success' },
          close: false
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          open: true,
          message: 'Failed to replace voicemail drop. Please try again.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
    }
  });
};

export const useRenameVoicemailDrop = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameVoicemailDrop(id, name),
    onSuccess: () => {
      // BOTH keys. This one, and the Power Dialer's own
      // `powerDialVoicemailDropsKey(kind)` — the picker reads that shorter key
      // and would keep showing the old name until something else refetched it.
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['voicemailDrops'] });
    },
    onError: () => {
      dispatch(
        openSnackbar({
          open: true,
          message: 'Could not rename that recording',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
    }
  });
};

export const useDeleteVoicemailDrop = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteVoicemailDrop(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      dispatch(
        openSnackbar({
          open: true,
          message: 'Voicemail drop deleted successfully',
          variant: 'alert',
          alert: { color: 'success' },
          close: false
        })
      );
    },
    onError: () => {
      dispatch(
        openSnackbar({
          open: true,
          message: 'Failed to delete voicemail drop. Please try again.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
    }
  });
};
